/**
 * CABFileList — Displays uploaded CAB files with metadata and actions.
 */

import React from 'react';
import { Text, Icon } from '@ui-library';
import { IconName } from '@ui-library/Icon/Icon.type';
import type { CABFile, CABFileCategory } from '../../types/cab';
import { IfHasPermission } from '../IfHasPermission';
import { Permissions } from '../../constants';

interface CABFileListProps {
  files: CABFile[];
  onDeleteFile: (fileId: string) => void;
  onDownloadFile?: (fileId: string, fileName: string) => void;
  disabled?: boolean;
  permissions?: string[];
}

/** Map file category to an icon. */
function getFileIcon(category: CABFileCategory): IconName {
  switch (category) {
    case 'document':
      return IconName.Document;
    case 'source_code':
      return IconName.Code;
    case 'archive':
      return IconName.Folder;
    case 'image':
      return IconName.Preview;
    default:
      return IconName.File;
  }
}

/** Format bytes to human-readable. */
function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
}

/** Format file category for display. */
function formatCategory(category: CABFileCategory): string {
  switch (category) {
    case 'document': return 'Document';
    case 'source_code': return 'Source Code';
    case 'archive': return 'Archive';
    case 'image': return 'Image';
    default: return 'File';
  }
}

export const CABFileList: React.FC<CABFileListProps> = ({
  files,
  onDeleteFile,
  onDownloadFile,
  disabled = false,
  permissions = [],
}) => {
  if (files.length === 0) return null;

  return (
    <div className="mt-bg-v2-md">
      <div className="flex justify-between items-center mb-bg-v2-sm">
        <Text
          variant="text-xs"
          weight="semibold"
          className="text-text-primary uppercase tracking-wider"
        >
          Uploaded Files ({files.length})
        </Text>
      </div>

      <div className="flex flex-col gap-bg-v2-xs">
        {files.map((file) => (
          <div
            key={file.file_id}
            className="
              flex items-center gap-bg-v2-sm
              p-bg-v2-sm px-bg-v2-md
              bg-bg-secondary rounded-bg-v2-md
              border border-transparent
              transition-all duration-200
              hover:bg-bg-tertiary hover:border-border-secondary
            "
          >
            {/* File icon */}
            <div className="w-8 h-8 bg-bg-brand-primary rounded-bg-v2-sm flex items-center justify-center shrink-0">
              <Icon
                name={getFileIcon(file.file_category)}
                size="sm"
                className="!text-text-brand-primary"
                inheritColor
              />
            </div>

            {/* File info */}
            <div className="flex-1 min-w-0">
              <Text
                variant="text-xs"
                weight="medium"
                className="text-text-primary truncate block"
              >
                {file.original_name}
              </Text>
              <Text variant="text-xs" className="text-text-tertiary">
                {formatSize(file.file_size)} &middot; {formatCategory(file.file_category)}
              </Text>
            </div>

            {/* Upload status badge */}
            <div className="shrink-0">
              <span
                className={`
                  inline-block px-bg-v2-sm py-bg-v2-xxs rounded-full text-bg-v2-xs font-bg-v2-medium
                  ${file.upload_status === 'completed'
                    ? 'bg-bg-success-primary text-text-success-primary'
                    : file.upload_status === 'uploading'
                      ? 'bg-bg-warning-primary text-text-warning-primary'
                      : 'bg-bg-error-primary text-text-error-primary'}
                `}
              >
                {file.upload_status === 'completed' ? 'Uploaded'
                  : file.upload_status === 'uploading' ? 'Uploading...'
                  : 'Error'}
              </span>
            </div>

            {/* Action buttons */}
            {disabled ? (
              <button
                type="button"
                onClick={() => onDownloadFile?.(file.file_id, file.original_name)}
                className="
                  p-bg-v2-xs rounded-bg-v2-sm
                  text-text-tertiary hover:text-text-brand-primary hover:bg-bg-brand-primary
                  transition-all duration-200
                "
                title="Download file"
              >
                <Icon name={IconName.Download} size="sm" inheritColor />
              </button>
            ) : (
              <IfHasPermission
                permissions={permissions}
                requires={Permissions.UpdateProjectPermission}
              >
                <button
                  type="button"
                  onClick={() => onDeleteFile(file.file_id)}
                  className="
                    p-bg-v2-xs rounded-bg-v2-sm
                    text-text-tertiary hover:text-text-primary hover:bg-bg-tertiary
                    transition-all duration-200
                  "
                  title="Remove file"
                >
                  <Icon name={IconName.XClose} size="sm" inheritColor />
                </button>
              </IfHasPermission>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};
